import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Link } from 'react-router-dom';
import RequestRow from './Components/RequestRow';
import AddRequestModal from '../Home/Components/AddRequestModal';
import api from '../api/api';

const RequestPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [viewingRequest, setViewingRequest] = useState(null);
  const [activeTab, setActiveTab] = useState("همه درخواست‌ها");
  const [data, setData] = useState({
    "تایید شده": [],
    "تایید نشده": [],
    "در انتظار": [],
    "ارجاعات": []
  });
  const [loading, setLoading] = useState(true);

  const columnStyles = {
    "تایید شده": { border: "border-green-500", text: "text-slate-900", badge: "bg-green-100 text-green-700" },
    "تایید نشده": { border: "border-red-500", text: "text-slate-900", badge: "bg-red-100 text-red-700" },
    "در انتظار": { border: "border-amber-400", text: "text-slate-900", badge: "bg-amber-100 text-amber-700" },
    "ارجاعات": { border: "border-blue-500", text: "text-slate-900", badge: "bg-blue-100 text-blue-700" }
  };

  const fetchRequests = async () => {
    try {
      const res = await api.get('/requests/');
      const grouped = {
        "تایید شده": res.data.filter(r => r.status === 'تایید شده'),
        "تایید نشده": res.data.filter(r => r.status === 'تایید نشده'),
        "در انتظار": res.data.filter(r => r.status === 'در انتظار'),
        "ارجاعات": res.data.filter(r => r.status === 'ارجاعات')
      };
      setData(grouped);
    } catch (error) {
      console.error('Error fetching requests:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const handleAddRequest = async (newRequest) => {
    try {
      await api.post('/requests/', { ...newRequest, status: 'در انتظار' });
      fetchRequests();
    } catch (error) {
      console.error('Error adding request:', error);
    }
  };

  const respondToRequest = async (flowId, status, comment) => {
    try {
      await api.patch(`/request-flows/${flowId}/`, { status, comment: comment || '' });
      setViewingRequest(null);
      fetchRequests();
    } catch (error) {
      console.error('خطا در پاسخ به درخواست:', error);
      window.alert('خطا در ثبت پاسخ. دوباره تلاش کنید.');
    }
  };

  const onDragEnd = async (result) => {
    const { source, destination } = result;
    if (!destination) return;
    const newData = { ...data };
    const [movedItem] = newData[source.droppableId].splice(source.index, 1);
    newData[destination.droppableId].splice(destination.index, 0, movedItem);
    setData(newData);
    try {
      await api.patch(`/requests/${movedItem.id}/`, { status: destination.droppableId });
    } catch (error) {
      console.error('Error updating request status:', error);
    }
  };

  const tabs = ["همه درخواست‌ها", "ارجاعات", "در انتظار", "تایید نشده", "تایید شده"];

  if (loading) return <div className="p-8 text-center">در حال بارگذاری...</div>;

  return (
    <div className="p-6 bg-slate-50 min-h-screen" dir="rtl">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-xl font-bold text-slate-800">همه درخواست‌ها</h1>
        <button onClick={() => setIsModalOpen(true)} className="bg-indigo-600 text-white px-6 py-2 rounded-xl text-sm font-bold shadow-lg hover:bg-indigo-700 transition-all">+ افزودن</button>
      </div>

      <div className="flex gap-8 mb-8 border-b border-slate-200">
        {tabs.map((tab) => (
          <button key={tab} onClick={() => setActiveTab(tab)} className={`pb-3 text-sm font-bold ${activeTab === tab ? "text-indigo-600 border-b-2 border-indigo-600" : "text-slate-500"}`}>{tab}</button>
        ))}
      </div>

      <DragDropContext onDragEnd={onDragEnd}>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {Object.keys(data).filter(col => activeTab === "همه درخواست‌ها" || col === activeTab).map((columnName) => (
            <Droppable key={columnName} droppableId={columnName}>
              {(provided) => (
                <div {...provided.droppableProps} ref={provided.innerRef} className="flex flex-col min-h-[500px]">
                  <div className={`flex justify-between items-center mb-4 pb-2 border-b-2 ${columnStyles[columnName].border}`}>
                    <Link to={`/requestsList?status=${columnName}`} className={`font-bold text-lg ${columnStyles[columnName].text}`}>
                      {columnName}
                    </Link>
                    <span className={`w-10 h-10 flex items-center justify-center rounded-full text-lg font-black shadow-md ${columnStyles[columnName].badge}`}>{data[columnName].length}</span>
                  </div>
                  {data[columnName].map((item, index) => (
                    <Draggable key={item.id} draggableId={item.id.toString()} index={index}>
                      {(provided) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          className="mb-3 cursor-pointer"
                          onClick={() => setViewingRequest(item)}
                        >
                          <RequestRow {...item} />
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          ))}
        </div>
      </DragDropContext>

      {isModalOpen && <AddRequestModal onClose={() => { setIsModalOpen(false); fetchRequests(); }} onAdd={handleAddRequest} />}

      {viewingRequest && (
        <AddRequestModal
          requestData={viewingRequest}
          respondMode={true}
          viewOnly={!viewingRequest.flows?.some((flow) => flow.status === 'pending')}
          onRespond={respondToRequest}
          onClose={() => setViewingRequest(null)}
        />
      )}
    </div>
  );
};

export default RequestPage;